<template>
  <div class="bg-white">
    <!-- Page Title & Overview -->
    <div class="py-16 bg-gray-50 overflow-hidden lg:py-24">
      <div class="relative max-w-xl mx-auto px-4 sm:px-6 lg:px-8 lg:max-w-7xl">
        <div class="relative">
          <h2 class="text-center text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
            {{ LeadIn.Title }}
          </h2>
          <p class="mt-4 max-w-3xl mx-auto text-center text-xl text-gray-500">
            {{ LeadIn.Overview }}
          </p>
        </div>
      </div>
    </div>
  </div>

  <!-- Culture -->
  <div class="bg-gray-100">
    <div class="max-w-7xl mx-auto py-24 sm:px-2 sm:py-16 lg:px-4">
      <div class="max-w-2xl mx-auto px-4 lg:max-w-none">
        <div class="grid grid-cols-1 items-center gap-y-10 gap-x-16 lg:grid-cols-2">
          <div>
            <h2 class="text-4xl font-extrabold tracking-tight text-gray-900">{{ Culture.LeadIn.Title }}</h2>
            <p class="mt-4 text-gray-500">{{ Culture.LeadIn.Overview }}</p>
          </div>
          <div class="aspect-w-3 aspect-h-2 bg-gray-100 rounded-lg overflow-hidden">
            <img src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-1.2.1&auto=format&fit=crop&w=1679&q=80" alt="Students collaborating" class="object-center object-cover" />
          </div>
        </div>
       
      </div>
    </div>
  </div>

  <!-- Benefits -->
  <div class="bg-gradient-to-b from-awtmtn-blue to-awtmtn-dark-blue">
    <div class="max-w-4xl mx-auto px-4 py-16 sm:px-6 sm:pt-20 sm:pb-24 lg:max-w-7xl lg:pt-24 lg:px-8">
      <h2 class="text-3xl font-extrabold text-white tracking-tight">
        {{ Benefits.LeadIn.Title }}
      </h2>
      <p class="mt-4 mx-auto max-w-3xl text-lg text-awtmtn-lite-blue">
        {{ Benefits.LeadIn.Overview }}
      </p>
      <div class="mt-12 grid grid-cols-1 gap-x-6 gap-y-12 sm:grid-cols-2 lg:mt-16 lg:grid-cols-3 lg:gap-x-8 lg:gap-y-16">
        <div v-for="benefit in Benefits.Content" :key="benefit.name">
          <div>
            <span class="flex items-center justify-center h-12 w-12 rounded-md bg-awtmtn-navy bg-opacity-20">
              <font-awesome-icon 
                :icon="benefit.icon" 
                class="h-7 w-7 text-white" 
                :aria-hidden="true" 
              />
            </span>
          </div>
          <div class="mt-6">
            <h3 class="text-lg font-medium text-white text-left">{{ benefit.name }}</h3>
            <p class="mt-2 text-base text-awtmtn-lite-blue text-left">
              <span v-html="benefit.description"></span>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Environment -->
  <div class="relative bg-gray-50 pt-16 pb-20 px-4 sm:px-6 lg:pt-24 lg:pb-28 lg:px-8">
    <div class="absolute inset-0">
      <div class="bg-white h-1/3 sm:h-2/3" />
    </div>
    <div class="relative max-w-7xl mx-auto">
      <div class="text-center">
        <h2 class="text-3xl tracking-tight font-extrabold text-gray-900 sm:text-4xl">{{ Environment.LeadIn.Title }}</h2>
        <p class="mt-3 max-w-2xl mx-auto text-xl text-gray-500 sm:mt-4">{{ Environment.LeadIn.Overview }}</p>
      </div>
      <div class="mt-12 max-w-lg mx-auto grid gap-5 lg:grid-cols-3 lg:max-w-none">
        <div v-for="post in Environment.Content" :key="post.title" class="flex flex-col rounded-lg shadow-lg overflow-hidden">
          <div class="flex-shrink-0">
            <img class="h-48 w-full object-cover" :src="post.imageUrl" alt="" />
          </div>
          <div class="flex-1 bg-white p-6 flex flex-col justify-between">
            <div class="flex-1">
                <p class="text-xl font-semibold text-gray-900">
                  {{ post.title }}
                </p>
                <p class="mt-3 text-base text-gray-500">
                  {{ post.description }}
                </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>


  <!-- Next Steps -->
  <div class="bg-awtmtn-navy">
    <div class="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8 text-center">
      <h2 class="text-3xl font-extrabold tracking-tight sm:text-4xl">
        <span class="block text-white">{{ Closing.LeadIn.Title }}</span>
      </h2>
      <p class="mt-4 text-awtmtn-lite-blue text-xl">
        <span v-html="Closing.LeadIn.Overview"></span>
      </p>
      <div class="mt-8">
        <div class="inline-flex rounded-md shadow mr-4">
          <a href="https://grnh.se/yc3fzxt88us" target="_blank" rel="noopener noreferrer" class="group inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-awtmtn-green hover:bg-opacity-90">
            IT Intern
          </a>
        </div>
        <div class="inline-flex rounded-md shadow">
          <a href="https://grnh.se/jd8k7t838us" target="_blank" rel="noopener noreferrer" class="group inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-awtmtn-green hover:bg-opacity-90">
            Platform Engineering Intern
          </a>
        </div>
      </div>
    </div>
  </div>
</template>






<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

interface Section {
  Title: string;
  Overview: string;
}

interface Environment {
  LeadIn: {
    Title: string;
    Overview: string;
  }
  Content: Array<{
    title: string;
    description: string;
    imageUrl: string;
  }>
}

interface Benefits {
  LeadIn: {
    Title: string;
    Overview: string;
  }
  Content: Array<{
    name: string;
    icon: string;
    description: string;
  }>
}

import { LeadIn, Culture, Benefits, Environment, Closing } from '../data/coop.json'

// Import FontAwesome icons
import { library } from '@fortawesome/fontawesome-svg-core'
import { faChartLine, faCode, faLightbulb, faRocket } from '@fortawesome/free-solid-svg-icons'

// Add icons to library
library.add(faChartLine, faCode, faLightbulb, faRocket)
</script>
